import * as React from 'react';
import { Text } from 'vet-bones/components';
import { alphaSort, getIntentClassName } from 'vet-bones/utils';
import { QueryBuilderModel } from 'vet-bones/utils/queryBuilder';

import { withConfirmation } from 'src/components/withConfirmation';
import { AttributesToRemoveInfo } from 'src/containers/ProjectDiagram/hooks/remove/types';
import { ModelRule } from 'src/containers/ProjectDiagram/types/data';
import {
  CommitTransform,
  Diagram,
} from 'src/containers/ProjectDiagram/types/diagram';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import { getQueryBuilderModel } from 'src/containers/ProjectDiagram/utils/getQueryBuilderModel';
import { getLabelForIri } from 'src/containers/ProjectDiagram/utils/labels';
import { isRuleValid } from 'src/containers/ProjectDiagram/utils/rules/isRuleValid';
import * as copy from 'src/templates/copy';

const getInvalidRules = (
  rulesById: Record<string, ModelRule>,
  ruleModel: QueryBuilderModel
) => Object.values(rulesById).filter((rule) => !isRuleValid(rule, ruleModel));

export const checkAndConfirmCommit = (
  model: Model,
  commit: CommitTransform,
  tname: string | undefined,
  attributesToRemoveInfo?: AttributesToRemoveInfo[],
  diagram?: Diagram
) => {
  return new Promise<boolean>((resolve) => {
    model.startTransaction(tname);
    const ruleModel = getQueryBuilderModel(model);
    const oldInvalidRuleIdsSet: Set<string> = ruleModel
      ? new Set(
          getInvalidRules(model.modelData.rulesById, ruleModel).map(
            (rule) => rule.id
          )
        )
      : new Set();

    // Headless commits pass no diagram; only model-only transforms run there.
    commit(model, diagram as Diagram);

    const updatedRuleModel = getQueryBuilderModel(model);
    const invalidRulesAfterCommit = ruleModel
      ? getInvalidRules(model.modelData.rulesById, updatedRuleModel)
      : [];
    const newlyInvalidRules = invalidRulesAfterCommit.filter(
      (rule) => !oldInvalidRuleIdsSet.has(rule.id)
    );

    // Check for no invalid rules and no attributes to remove if attributesToRemoveInfo exists
    if (
      !newlyInvalidRules.length &&
      (attributesToRemoveInfo
        ? attributesToRemoveInfo?.every(
            (info) => !info.attributesToRemove.length
          )
        : true)
    ) {
      model.commitTransaction(tname);
      resolve(true);
      return;
    }

    const { labelsByIri } = model.modelData;
    const message = [];

    attributesToRemoveInfo?.forEach((info) => {
      if (info.attributesToRemove.length) {
        message.push(
          <div key={info.attributesToRemove[0].value}>
            <Text>
              {copy.components.projectDiagram.modificationWarning.attributesMessage(
                getLabelForIri(labelsByIri, info.classIri)
              )}
            </Text>
            <ul>
              {info?.attributesToRemove.map((attribute) => (
                <li key={attribute.value}>
                  {getLabelForIri(labelsByIri, attribute.value)}
                </li>
              ))}
            </ul>
          </div>
        );
      }
    });

    if (newlyInvalidRules.length) {
      message.push(
        <div key={newlyInvalidRules[0].id}>
          <Text>
            {copy.components.projectDiagram.modificationWarning.rulesMessage}
          </Text>
          <ul>
            {newlyInvalidRules
              .sort(alphaSort((rule) => rule.name))
              .map((rule) => (
                <li key={rule.id}>{rule.name}</li>
              ))}
          </ul>
        </div>
      );
    }

    withConfirmation({
      dialogClassName: 'sd-dialog-remove-class-warning',
      isCloseButtonShown: false,
      message,
      noPrompt: copy.components.projectDiagram.modificationWarning.cancel,
      title: null,
      yesButtonClassName: getIntentClassName('sd-green'),
      yesPrompt: copy.components.projectDiagram.modificationWarning.proceed,
      onConfirmationSuccess: () => {
        model.commitTransaction(tname);
        resolve(true);
      },
      onConfirmationFailure: () => {
        model.rollbackTransaction();
        resolve(false);
      },
    });
  });
};
